import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../styles/ReportesFinancierosStyles/ReportesFinancieros.module.css';
import Sidebar, { useSidebar } from '../../components/Sidebar';
import HeaderGlobal from '../../components/HeaderGlobal';
import LoaderOverlay from '../../components/LoaderOverlay';
import ReportFilters from './ReportFilters';
import ReportTable from './ReportTable';
import ReportSummary from './ReportSummary';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

function ReportesFinancierosScreen() {
  const { isCollapsed } = useSidebar();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    id_campus: '',
    id_seccion: '',
    id_programa: '',
    anio: '',
  });

  const handleFilterChange = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleGenerateReport = useCallback(async () => {
    if (!filters.anio) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams();
      params.append('anio', filters.anio);
      if (filters.id_campus) params.append('id_campus', filters.id_campus);
      if (filters.id_seccion) params.append('id_seccion', filters.id_seccion);
      if (filters.id_programa) params.append('id_programa', filters.id_programa);

      const res = await fetch(`${API_BASE}/financiero/reportes/becas/?${params.toString()}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error('Error al generar el reporte');
      const data = await res.json();
      setReportData(data);
    } catch (error) {
      console.error('Error generando reporte:', error);
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleExportCSV = useCallback(() => {
    if (!reportData || !reportData.estudiantes || reportData.estudiantes.length === 0) return;

    const headers = [
      'Matrícula', 'Nombre del Estudiante', 'Tipo de Beca', 'Porcentaje de Beca',
      'Monto de Beca', 'Ciclo Escolar Ingreso', 'Fecha de Inicio de la Beca',
      'Fecha de Término de la Beca', 'Costo de la Colegiatura (Parcialidad)',
      'Cantidad que Paga el Estudiante', 'Cantidad Pagada en el Año'
    ];

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = reportData.estudiantes.map(e => [
      escapeCSV(e.matricula),
      escapeCSV(e.nombre_completo),
      escapeCSV(e.nombre_beca || e.tipo_beca),
      e.porcentaje_otorgado ? `${e.porcentaje_otorgado}%` : '',
      e.monto_beca_asignado ? `$${Number(e.monto_beca_asignado).toFixed(2)}` : '',
      escapeCSV(e.ciclo_ingreso),
      escapeCSV(e.fecha_inicio),
      escapeCSV(e.fecha_fin),
      `$${Number(e.costo_colegiatura_parcialidad || 0).toFixed(2)}`,
      `$${Number(e.cantidad_pagada_estudiante || 0).toFixed(2)}`,
      `$${Number(e.cantidad_pago_anual || 0).toFixed(2)}`,
    ].join(','));

    // Resumen
    rows.push('');
    rows.push('Valor Anual');
    rows.push('Número de destinatarios por tipo de Beca');
    rows.push('Tipo de Beca,Destinatarios');
    if (reportData.resumen_becas) {
      Object.entries(reportData.resumen_becas).forEach(([beca, count]) => {
        rows.push(`${escapeCSV(beca)},${count}`);
      });
    }
    rows.push(`Total,${reportData.total_becas}`);

    const BOM = '\uFEFF';
    const csvContent = BOM + [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Reporte_Becas_${filters.anio || 'general'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }, [reportData, filters.anio]);

  return (
    <div className={styles.mainContainer}>
      <div className={`${styles.layout} ${isCollapsed ? styles.sidebarCollapsedLayout : ''}`}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <main className={styles.mainContent}>
            <HeaderGlobal title="Reportes Financieros" />
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} label="Generando reporte..." />

                <ReportFilters
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onGenerate={handleGenerateReport}
                />

                {reportData ? (
                  <div className={styles.reportContainer}>
                    <div className={styles.reportHeader}>
                      <h2 className={styles.reportTitle}>REPORTE DE BECAS</h2>
                    </div>

                    <ReportTable
                      estudiantes={reportData.estudiantes}
                      filters={filters}
                    />

                    <ReportSummary
                      resumenBecas={reportData.resumen_becas}
                      totalBecas={reportData.total_becas}
                    />

                    <div className={styles.actionsRow}>
                      <button
                        className={styles.csvButton}
                        onClick={handleExportCSV}
                        disabled={!reportData.estudiantes || reportData.estudiantes.length === 0}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Descargar CSV
                      </button>
                    </div>
                  </div>
                ) : (
                  !loading && (
                    <div className={styles.reportContainer}>
                      <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>📊</div>
                        <p className={styles.emptyText}>Selecciona los filtros y genera el reporte</p>
                        <p className={styles.emptySubText}>Elige al menos el año para generar el reporte de becas</p>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default ReportesFinancierosScreen;
